import React from 'react';
import { ScrollView, Platform, TouchableOpacity } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, useThemeName } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';
import { ArrowLeft, Settings } from 'lucide-react-native';

// Types
import type { RootStackScreenProps } from '@/types/navigation';

// Components
import { Screen, ScreenHeader, LanguageSwitcher, ThemeSwitcher, PushNotificationToggle, DeleteAccountButton, SvgBackground } from '@/components';
import { useIsMobileLayout } from '@/hooks/useWindowWidth';
import { COLORS } from '@/constants/colors';

// Contexts
import { useThemeContext } from '@/contexts/ThemeContext';

type Props = RootStackScreenProps<'ApplicationSettings'>;

const ApplicationSettingsScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const { toggleTheme } = useThemeContext();
  const themeName = useThemeName();
  const isMobileLayout = useIsMobileLayout();
  const isDark = themeName === 'dark';
  
  const bgColor = isDark ? COLORS.BLACK : '#f5f5f5';

  const handlePushNotificationToggle = (_enabled: boolean) => {
  };

  const handleDeleteAccount = () => {
  };

  const scrollViewContentStyle = { paddingTop: 24, paddingBottom: 100, paddingHorizontal: 16 };

  return (
    <Screen edges={['top']}>
      <YStack flex={1} backgroundColor={bgColor}>
        {/* Header */}
        {(Platform.OS === 'web' && !isMobileLayout) ? (
          <ScreenHeader
            title={t('screens.appSettings.title')}
            onBackPress={() => navigation.goBack()}
          />
        ) : (
          <SvgBackground
            offsetLeft={-300}
            offsetTop={-250}
            height={180}
            variant="flat"
            borderBottomLeftRadius={24}
            borderBottomRightRadius={24}
          >
            <XStack
              paddingTop="$10"
              paddingHorizontal="$4"
              paddingBottom="$3"
              alignItems="center"
              space="$3"
              width="100%"
            >
              <TouchableOpacity onPress={() => navigation.goBack()}>
                <ArrowLeft size={24} color="white" />
              </TouchableOpacity>
              <Settings size={24} color="white" />
              <Text
                fontSize={28}
                fontWeight="bold"
                color="white"
                fontFamily="Poppins"
                numberOfLines={1}
                adjustsFontSizeToFit
                minimumFontScale={0.8}
                flexShrink={1}
              >
                {t('screens.appSettings.title')}
              </Text>
            </XStack>
          </SvgBackground>
        )}

      <YStack flex={1}>
        <ScrollView
          contentContainerStyle={scrollViewContentStyle}
          showsVerticalScrollIndicator={false}
        >
          <YStack
            backgroundColor="$backgroundStrong"
            borderTopLeftRadius="$10"
            borderTopRightRadius="$10"
            paddingTop="$6"
            paddingHorizontal="$4"
            space="$6"
            marginHorizontal="$3"
            borderWidth={themeName === 'dark' ? 0 : 1}
            borderColor={themeName === 'dark' ? 'transparent' : '#e5e7eb'}
          >
        <YStack space="$3">
          <Text fontSize="$5" fontWeight="600" color={themeName === 'dark' ? '#ffffff' : '#18181b'}>
            {t('screens.appSettings.language')}
          </Text>
          <Text fontSize="$3" color={themeName === 'dark' ? '#a1a1aa' : '#71717a'} lineHeight="$4">
            {t('screens.appSettings.languageDesc')}
          </Text>
          <XStack justifyContent="flex-start">
            <LanguageSwitcher />
          </XStack>
        </YStack>

        <YStack space="$3">
          <Text fontSize="$5" fontWeight="600" color={themeName === 'dark' ? '#ffffff' : '#18181b'}>
            {t('screens.appSettings.theme')}
          </Text>
          <Text fontSize="$3" color={themeName === 'dark' ? '#a1a1aa' : '#71717a'} lineHeight="$4">
            {t('screens.appSettings.themeDesc')}
          </Text>
          <XStack justifyContent="flex-start">
            <ThemeSwitcher onToggle={toggleTheme} />
          </XStack>
        </YStack>

        <YStack space="$3">
          <Text fontSize="$5" fontWeight="600" color={themeName === 'dark' ? '#ffffff' : '#18181b'}>
            {t('screens.appSettings.pushNotifications')}
          </Text>
          <Text fontSize="$3" color={themeName === 'dark' ? '#a1a1aa' : '#71717a'} lineHeight="$4">
            {t('screens.appSettings.pushNotificationsDesc')}
          </Text>
          <XStack justifyContent="flex-start">
            <PushNotificationToggle onToggle={handlePushNotificationToggle} />
          </XStack>
        </YStack>

        <YStack space="$3">
          <Text fontSize="$5" fontWeight="600" color={themeName === 'dark' ? '#ffffff' : '#18181b'}>
            {t('screens.appSettings.deleteAccount')}
          </Text>
          <Text fontSize="$3" color={themeName === 'dark' ? '#a1a1aa' : '#71717a'} lineHeight="$4">
            {t('screens.appSettings.deleteAccountDesc')}
          </Text>
          <XStack justifyContent="flex-start">
            <DeleteAccountButton onDelete={handleDeleteAccount} />
          </XStack>
        </YStack>

        <YStack
          backgroundColor="$gray4"
          padding="$4"
          borderRadius="$4"
          space="$2"
          marginTop="$4"
        >
          <Text fontSize="$4" fontWeight="600" color={themeName === 'dark' ? '#ffffff' : '#18181b'}>
            {t('screens.appSettings.info')}
          </Text>
          <Text fontSize="$3" color={themeName === 'dark' ? '#a1a1aa' : '#71717a'} lineHeight="$4">
            {t('screens.appSettings.infoDesc')}
          </Text>
        </YStack>
          </YStack>
        </ScrollView>
      </YStack>
      </YStack>
    </Screen>
  );
};

export default ApplicationSettingsScreen;  